import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast, type Id } from 'react-toastify';
import { useGetReservationByToken } from '@/hooks/useGetReservationByToken';
import { Home } from '@/templates/Home';
import { useModalAuthStore } from '@/store/auth/authModalStore';
import { useModalShoppingStore } from '@/store/shopping/modalShoppingStore';
import { useToken } from '@/store/auth/tokenStore';
import { useShoppingCartStore } from '@/store/shoppingCart/shoppingCartStore';
import { useReservationStore } from '@/store/reservation/reservationStore';

export default function ReservationValidation() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { token } = useToken();
    const { setIsVisibleAuthModal } = useModalAuthStore();
    const { setIsVisibleShoppingWave } = useModalShoppingStore();
    const { setCurrentDate } = useShoppingCartStore();
    const { setActiveReservation, clearActiveReservation } = useReservationStore();
    const loadingToastRef = useRef<Id | null>(null);
    const loginPromptShownRef = useRef(false);
    const finalStatusShownRef = useRef(false);
    const missingTokenToastShownRef = useRef(false);
    const queryToken =
        searchParams.get('token') || searchParams.get('reservationToken');
    const reservationToken = queryToken || undefined;

    useEffect(() => {
        if (!reservationToken) {
            if (
                location.pathname.startsWith('/reserva') &&
                !missingTokenToastShownRef.current
            ) {
                toast.error('Link de reserva inválido.');
                missingTokenToastShownRef.current = true;
            }
            return;
        }

        if (!token && !loginPromptShownRef.current) {
            setIsVisibleAuthModal(true, 'login');
            toast.info('Faça login para validar sua reserva.');
            loginPromptShownRef.current = true;
        }
    }, [reservationToken, token, setIsVisibleAuthModal, location.pathname]);

    const reservationQuery = useGetReservationByToken(
        reservationToken,
        Boolean(token)
    );

    useEffect(() => {
        if (!reservationToken || !token) return;

        if (reservationQuery.isPending && !loadingToastRef.current) {
            loadingToastRef.current = toast.loading('Validando reserva...');
            finalStatusShownRef.current = false;
        }

        if (reservationQuery.isSuccess && !finalStatusShownRef.current) {
            setActiveReservation(reservationQuery.data);
            setCurrentDate(new Date(reservationQuery.data.checkIn));
            setIsVisibleShoppingWave(true);
            if (loadingToastRef.current) {
                toast.update(loadingToastRef.current, {
                    render: 'Reserva validada com sucesso.',
                    type: 'success',
                    isLoading: false,
                    autoClose: 4000,
                });
                loadingToastRef.current = null;
            } else {
                toast.success('Reserva validada com sucesso.');
            }
            finalStatusShownRef.current = true;
        }

        if (reservationQuery.isError && !finalStatusShownRef.current) {
            clearActiveReservation();
            const message =
                reservationQuery.error instanceof Error
                    ? reservationQuery.error.message
                    : 'Não foi possível validar a reserva.';

            if (loadingToastRef.current) {
                toast.update(loadingToastRef.current, {
                    render: message,
                    type: 'error',
                    isLoading: false,
                    autoClose: 6000,
                });
                loadingToastRef.current = null;
            } else {
                toast.error(message);
            }
            finalStatusShownRef.current = true;
        }
    }, [
        reservationToken,
        token,
        reservationQuery.isPending,
        reservationQuery.isSuccess,
        reservationQuery.isError,
        reservationQuery.error,
        reservationQuery.data,
        setActiveReservation,
        clearActiveReservation,
        setCurrentDate,
        setIsVisibleShoppingWave,
    ]);

    return <Home />;
}
